import type useLifestyleFilters from "../../hooks/useLifestyleFilters";
import {
  BUCKET_FILTERS,
  FACET_ROWS,
  NUMERIC_FILTERS,
  type BucketKey,
  type NumericKey,
  type PrizeCategory,
  type RangeKey,
} from "../../lifestyle-constants";

/** One filter within a category: the two facet lists, plus the measurements. */
export type FilterScreenKey = "makes" | "styles" | NumericKey;

function isBucketKey(key: FilterScreenKey): key is BucketKey {
  return BUCKET_FILTERS.some(filter => filter.key === key);
}

export interface FilterScreenDefinition {
  key: FilterScreenKey;
  label: string;
}

/**
 * Where the drawer currently is. The design drills three levels deep — the
 * category list, one category's filters, then a single filter — so the drawer
 * navigates rather than stacking every control onto one scrolling panel.
 */
export type DrawerRoute =
  | { screen: "lobby" }
  | { screen: "category"; category: PrizeCategory }
  | { screen: "filter"; category: PrizeCategory; filter: FilterScreenDefinition };

type Filters = ReturnType<typeof useLifestyleFilters>;

/**
 * The filter rows for a category, in design order: the facet lists first, then
 * whichever measurements apply. Rows with nothing behind them are dropped, so
 * a category never offers a screen that would open empty.
 */
export function filterScreensFor(category: PrizeCategory, filters: Filters): FilterScreenDefinition[] {
  const screens: FilterScreenDefinition[] = [];

  for (const row of FACET_ROWS[category]) {
    const facets = row.key === "makes" ? filters.makeFacets : filters.styleFacets;
    if (facets.length > 0) screens.push({ key: row.key, label: row.label });
  }

  for (const numeric of NUMERIC_FILTERS) {
    const appliesToCategory = numeric.categories.length === 0 || numeric.categories.includes(category);
    if (!appliesToCategory) continue;

    const applicable =
      numeric.kind === "bucket" ? filters.bucketApplicable[numeric.key] : filters.bounds[numeric.key].applicable;

    if (applicable) screens.push({ key: numeric.key, label: numeric.label });
  }

  return screens;
}

/**
 * How many selections a filter is holding. Drives the badge on a category
 * row, the number inside a chip, and the count in the screen title. A range
 * counts as one because it is a single min/max, however wide.
 */
export function selectionCountFor(key: FilterScreenKey, filters: Filters): number {
  if (key === "makes") return filters.filters.makes.length;
  if (key === "styles") return filters.filters.styles.length;
  if (isBucketKey(key)) return (filters.filters.buckets[key] ?? []).length;
  return filters.filters.ranges[key as RangeKey] ? 1 : 0;
}

/** Total selections across a category, for the "Cars (15)" title. */
export function categorySelectionCount(category: PrizeCategory, filters: Filters): number {
  return filterScreensFor(category, filters).reduce(
    (total, screen) => total + selectionCountFor(screen.key, filters),
    0
  );
}

/** Drops everything a single filter is holding, behind a chip's X. */
export function clearFilterScreen(key: FilterScreenKey, filters: Filters): void {
  if (key === "makes" || key === "styles") {
    filters.clear(key);
    return;
  }
  if (isBucketKey(key)) {
    filters.clearBucket(key);
    return;
  }
  filters.setRange(key as RangeKey, null);
}
